// The one seam between the dashboard screens and its data.
//
// Every screen reads data only through `DashboardApi`, one method per route in
// docs/api/dashboard-contract.md. Today `createFixtureApi()` (./fixtures.ts)
// implements it in memory. The live build (jal-frontend, B2) adds
// `createLiveApi(token)` on the typed Hono client and swaps it in at
// `createApi` below; no screen changes.
//
// Rules every implementation keeps:
//   - a non-2xx response rejects with ApiError (status plus the `error` text),
//     a 401 in particular, which the shell turns into the token screen;
//   - `validateToken` resolves false on 401 and never stores anything itself
//     (the shell owns the token in sessionStorage, see ./session.ts);
//   - methods return the contract shapes unchanged, masked values included.
import { createContext, useContext } from "react";
import type {
  Connection,
  DataPage,
  Demografi,
  DemografiScope,
  NikCheck,
  SubmissionDetail,
  Issue,
  Ok,
  Overview,
  PairingCode,
  PairingCodeFilter,
  Sender,
  SenderFilter,
  SenderPii,
  SubmissionFilters,
  SubmissionPage,
  SubmissionPii,
} from "./types";

/** The CSV body of GET /dashboard/submissions.csv, ready to save. */
export interface CsvExport {
  /** From Content-Disposition, "submissions-YYYYMMDD.csv". */
  filename: string;
  /** text/csv; charset=utf-8 with the UTF-8 BOM. */
  blob: Blob;
}

export interface DashboardApi {
  /** GET /dashboard/session with the given bearer: true on 200, false on 401. */
  validateToken(token: string): Promise<boolean>;
  /** GET /dashboard/overview */
  overview(): Promise<Overview>;
  /** GET /dashboard/submissions */
  listSubmissions(filters: SubmissionFilters): Promise<SubmissionPage>;
  /** GET /dashboard/submissions/:id/reveal: the full phone and NIK of one row.
   *  Audited and rate limited server-side: 404, 429 (Retry-After), 503. */
  revealSubmission(id: string): Promise<SubmissionPii>;
  /** GET /dashboard/submissions.csv, same filters, no paging. */
  exportCsv(filters: SubmissionFilters): Promise<CsvExport>;
  /** GET /dashboard/pairing-codes?status= */
  listCodes(status: PairingCodeFilter): Promise<{ items: PairingCode[] }>;
  /** POST /dashboard/pairing-codes, label 1..64. The code never expires; it is valid until used once or deleted. */
  createCode(label: string): Promise<PairingCode>;
  /** DELETE /dashboard/pairing-codes/:id (404 not found, 409 already used) */
  deleteCode(id: string): Promise<Ok>;
  /** DELETE /dashboard/submissions/:id, permanent and audited (404 when missing). */
  deleteSubmission(id: string): Promise<Ok>;
  /** GET /dashboard/senders?status= */
  listSenders(status: SenderFilter): Promise<{ items: Sender[] }>;
  /** GET /dashboard/senders/:id/reveal: the full phone of one sender (same limits). */
  revealSender(id: string): Promise<SenderPii>;
  /** POST /dashboard/senders/:id/revoke */
  revokeSender(id: string): Promise<Ok>;
  /** POST /dashboard/senders/:id/restore */
  restoreSender(id: string): Promise<Ok>;
  /** GET /dashboard/connection: the bot number's linked-device state. */
  connection(): Promise<Connection>;
  /** POST /dashboard/connection/pairing-code { phone }: 200 status "pairing"
   *  with the 8-character code; 400 bad number (issues), 409 already_linked,
   *  400 not_bot_phone (the number is not the locked bot number), 422 cloud_transport,
   *  429 rate_limited, 503 pairing_unavailable or rate_limit_unavailable. */
  requestPairingCode(phone: string): Promise<Connection>;
  /** GET /dashboard/demografi?prov=&kab=: aggregates for one scope (30 s server cache). */
  demografi(scope: DemografiScope): Promise<Demografi>;
  /** GET /dashboard/demografi/data: one row per person matching the filters, full NIK. */
  listData(scope: DemografiScope, page: number, pageSize: number): Promise<DataPage>;
  /** GET /dashboard/demografi/kiriman/:id: the full extraction of one submission (404 when missing). */
  submissionDetail(id: string): Promise<SubmissionDetail>;
  /** POST /dashboard/demografi/cek { nik, jev }: decode, derive, rules, JEV on soft flags. */
  checkNik(nik: string, jev: boolean): Promise<NikCheck>;
  /** POST /dashboard/connection/logout: unlinks the device (422 under cloud). */
  logoutConnection(): Promise<Connection>;
}

/** A non-2xx response, carrying the status and the contract's `error` text. */
export class ApiError extends Error {
  readonly status: number;
  /** The field problems of a 400 invalid_request, when the body had them. */
  readonly issues: Issue[];
  constructor(status: number, message: string, issues: Issue[] = []) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.issues = issues;
  }
}

export function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

/** Builds the api for a stored token. B2 replaces the body with the live client. */
export type ApiFactory = (token: string) => DashboardApi;

export const DashboardApiContext = createContext<DashboardApi | null>(null);

export function useDashboardApi(): DashboardApi {
  const api = useContext(DashboardApiContext);
  if (!api) throw new Error("useDashboardApi: no DashboardApiContext provider above this screen");
  return api;
}
